import React, { useState, useEffect } from 'react';
import ReactGA from 'react-ga4';
import { units } from '../data/units';
import SelectedUnitsPanel from './SelectedUnitsPanel';
import ResultPanel from './ResultPanel';

// Calculator component: Main React component for the IAL Maths Eligibility Calculator UI and logic.
// Props:
// - selectedUnits: array of currently selected unit codes
// - setSelectedUnits: function to update selected units
// - result: eligibility result object
// - setResult: function to update eligibility result
// - expandStates: object tracking which unit panels are expanded
// - setExpandStates: function to update expandStates
// - onStartOver: function to reset the calculator
// - guidedContext: object containing guided context information
function Calculator({ 
  selectedUnits, 
  setSelectedUnits, 
  result, 
  setResult, 
  expandStates, 
  setExpandStates,
  onStartOver,
  guidedContext
}) {
  // --- State Hooks ---
  // qualificationMode: 'standard' for single qualification, 'dual' for dual mode
  const [qualificationMode, setQualificationMode] = useState('standard'); // 'standard' or 'dual'
  // selectedQualification: tracks which additional qualification option is selected
  const [selectedQualification, setSelectedQualification] = useState('ial+ias');
  // showQualOptions: controls visibility of the additional qualification options panel
  const [showQualOptions, setShowQualOptions] = useState(false);
  // feedbackGiven: tracks if user has given feedback on the page
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  
  // Determine view mode from guidedContext
  const isSingleCode = guidedContext && guidedContext.cashInType === 'Single code';
  const isDualCode = guidedContext && guidedContext.cashInType === 'Multiple codes';

  // If single code, force standard mode and hide dual mode options
  useEffect(() => {
    if (isSingleCode) {
      setQualificationMode('standard');
      setShowQualOptions(false);
    } else if (isDualCode) {
      setQualificationMode('dual');
      setShowQualOptions(true);
    }
    // eslint-disable-next-line
  }, [isSingleCode, isDualCode]);

  // toggleExpand: Expands or collapses a unit section (Pure, Further, Applied) and tracks the event in Google Analytics.
  const toggleExpand = (section) => {
    // Track section expansion/collapse
    ReactGA.event({
      category: 'UI',
      action: expandStates[section] ? 'Collapse Section' : 'Expand Section',
      label: section
    });
    
    setExpandStates({
      ...expandStates,
      [section]: !expandStates[section]
    });
  };

  // toggleUnit: Adds or removes a unit from the selectedUnits array and tracks the event in Google Analytics.
  const toggleUnit = (unitCode) => {
    // Track unit selection/deselection
    ReactGA.event({
      category: 'Unit',
      action: selectedUnits.includes(unitCode) ? 'Deselect Unit' : 'Select Unit',
      label: unitCode
    });
    
    if (selectedUnits.includes(unitCode)) {
      setSelectedUnits(selectedUnits.filter(code => code !== unitCode));
    } else {
      setSelectedUnits([...selectedUnits, unitCode]);
    }
  };

  // checkIALEligibility: Main entry point for eligibility checking. Tracks the event, validates input, and delegates to standard or dual mode logic.
  const checkIALEligibility = () => {
    try {
      // Track the eligibility check event
      ReactGA.event({
        category: 'Calculator',
        action: 'Check Eligibility',
        label: JSON.stringify({
          units: selectedUnits,
          mode: qualificationMode,
          qualification: selectedQualification
        })
      });

      if (selectedUnits.length === 0) {
        throw new Error("Please select at least one unit");
      }

      if (qualificationMode === 'standard') {
        checkStandardEligibility();
      } else {
        checkDualEligibility();
      }
    } catch (error) {
      // Track errors in eligibility check
      ReactGA.event({
        category: 'Error',
        action: 'Eligibility Check Error',
        label: error.message
      });
      
      // Enhanced error handling with actionable steps
      let enhancedMessage = error.message;
      let alternativeMessage = "";
      
      if (error.message.includes("Please select at least one unit")) {
        enhancedMessage = "No units selected";
        alternativeMessage = "Start by selecting units from the Pure Mathematics, Further Pure Mathematics, or Applied Mathematics sections above. You need at least one unit to check eligibility.";
      } else if (error.message.includes("network") || error.message.includes("fetch")) {
        enhancedMessage = "Connection issue detected";
        alternativeMessage = "Please check your internet connection and try again. If the problem persists, try refreshing the page.";
      } else {
        alternativeMessage = "Try selecting different unit combinations or contact support if this issue continues.";
      }
      
      setResult({
        eligible: false,
        message: enhancedMessage,
        alternativeMessage: alternativeMessage
      });
    }
  };

  // checkStandardEligibility: Handles eligibility logic for standard (single qualification) mode.
  // Contains logic for all single-qualification awards and provides detailed feedback for missing requirements.
  const checkStandardEligibility = () => {
    // Define constant arrays at the top
    const pureUnits = ["P1", "P2", "P3", "P4"];
    const furtherPureUnits = ["FP1", "FP2", "FP3"];
    const appliedUnits = ["S1", "S2", "S3", "M1", "M2", "M3", "D1"].filter(unit => 
      selectedUnits.includes(unit)
    );

    // Check Further Pure requirements first
    const hasFP1 = selectedUnits.includes("FP1");
    
    // Count further pure units (FP1, FP2, FP3)
    const selectedFurtherPureUnits = furtherPureUnits.filter(unit => 
      selectedUnits.includes(unit)
    );

    // Check for P1 and P2
    const hasP1P2 = ["P1", "P2"].every(unit => selectedUnits.includes(unit));

    // Check for all P units
    const hasPureComplete = pureUnits.every(unit => selectedUnits.includes(unit));

    // For YFM01 (IAL Further Mathematics)
    // Must have either:
    // 1. All three FP units plus three applied units, OR
    // 2. Two FP units plus four applied units
    const hasTwoFP = selectedFurtherPureUnits.length >= 2;
    const hasAllFP = selectedFurtherPureUnits.length === 3;
    const totalAppliedUnits = appliedUnits.length;
    const isEligibleForYFM01 = (hasAllFP && totalAppliedUnits >= 3) || 
                              (hasTwoFP && totalAppliedUnits >= 4);

    // Check YFM01 eligibility first
    if (isEligibleForYFM01) {
      setResult({
        eligible: true,
        message: "You are eligible for the IAL Further Mathematics qualification (YFM01)!",
        qualification: "IAL Further Mathematics (YFM01)"
      });
      return;
    }

    // Check for valid applied pairs for IAL
    const validPairs = [
      ["S1", "S2"], ["M1", "M2"], ["S1", "M1"], 
      ["S1", "D1"], ["M1", "D1"]
    ];
    const hasValidPair = validPairs.some(pair => 
      pair.every(unit => selectedUnits.includes(unit))
    );

    // Check for YPM01 eligibility (P1-P4, FP1, and FP2 or FP3)
    const hasFP2 = selectedUnits.includes("FP2");
    const hasFP3 = selectedUnits.includes("FP3");
    const hasYPM01 = hasPureComplete && selectedUnits.includes("FP1") && (hasFP2 || hasFP3);

    // Check for XPM01 eligibility (P1, P2, and FP1 only)
    const hasXPM01 = hasP1P2 && selectedUnits.includes("FP1") && !hasFP2 && !hasFP3;

    // Check if eligible for both YMA01 and YPM01 (conflict case)
    const isEligibleForYMA01 = hasPureComplete && hasValidPair;
    if (hasYPM01 && isEligibleForYMA01) {
      setResult({
        eligible: true,
        message: "You are eligible for either IAL Mathematics (YMA01) or IAL Pure Mathematics (YPM01), but not both in the same series.",
        qualification: "IAL Mathematics (YMA01) or IAL Pure Mathematics (YPM01)"
      });
      return;
    }

    // Check YPM01 eligibility first
    if (hasYPM01) {
      setResult({
        eligible: true,
        message: "You are eligible for the IAL Pure Mathematics qualification (YPM01)!",
        qualification: "IAL Pure Mathematics (YPM01)"
      });
      return;
    }


    // For XFM01 (IAS Further Mathematics)
    // Must have FP1 plus two additional units (can be further pure or applied)
    const totalUnitsForXFM01 = (selectedFurtherPureUnits.length - 1) + appliedUnits.length; // -1 because we don't count FP1 twice
    const isEligibleForXFM01 = hasFP1 && totalUnitsForXFM01 >= 2;

    // Check XFM01 eligibility
    if (isEligibleForXFM01) {
      setResult({
        eligible: true,
        message: "You are eligible for the IAS Further Mathematics qualification (XFM01)!",
        qualification: "IAS Further Mathematics (XFM01)"
      });
      return;
    }

    // Check XPM01 eligibility before IAS Mathematics
    if (hasXPM01) {
      setResult({
        eligible: true,
        message: "You are eligible for the IAS Pure Mathematics qualification (XPM01)!",
        qualification: "IAS Pure Mathematics (XPM01)"
      });
      return;
    }

    // Check if user is eligible for IAS Mathematics
    const isEligibleForIAS = hasP1P2 && appliedUnits.length >= 1;

    if (!hasPureComplete && isEligibleForIAS) {
      setResult({
        eligible: true,
        message: "You are eligible for the IAS Mathematics qualification (XMA01), but not for the full IAL Mathematics (YMA01).",
        qualification: "IAS Mathematics (XMA01)",
        alternativeMessage: "To be eligible for IAL Mathematics (YMA01), you need to complete all Pure units (P1-P4) and have a valid applied pair."
      });
      return;
    }

    if (!hasPureComplete) {
      let message = "Missing one or more required Pure Mathematics units (P1-P4)";
      
      // If they have some pure units, be more specific
      if (pureUnits.filter(unit => selectedUnits.includes(unit)).length > 0) {
        const missingUnits = pureUnits.filter(unit => !selectedUnits.includes(unit));
        message = `Missing ${missingUnits.join(", ")} from the required Pure Mathematics units`;
      }
      
      // Add recommendations if they're close to IAS
      if (pureUnits.filter(unit => selectedUnits.includes(unit)).length >= 1) {
        const neededForIAS = [];
        if (!selectedUnits.includes("P1")) neededForIAS.push("P1");
        if (!selectedUnits.includes("P2")) neededForIAS.push("P2");
        if (appliedUnits.length === 0) neededForIAS.push("one applied unit");
        
        message += `. You could aim for IAS Mathematics (XMA01) by adding ${neededForIAS.join(" and ")}.`;
      }
      
      setResult({
        eligible: false,
        message: message
      });
      return;
    }

    if (!hasValidPair) {
      // Construct helpful message about what pairs they could add
      let message = "Missing a valid applied pair. You need one of: ";
      const possiblePairs = [];
      
      validPairs.forEach(pair => {
        const havePair = pair.filter(unit => selectedUnits.includes(unit));
        const missingFromPair = pair.filter(unit => !selectedUnits.includes(unit));
        
        if (havePair.length === 1) {
          possiblePairs.push(`${havePair[0]}+${missingFromPair[0]}`);
        } else if (havePair.length === 0) {
          possiblePairs.push(`${pair[0]}+${pair[1]}`);
        }
      });
      
      message += possiblePairs.join(", ");
      
      setResult({
        eligible: false,
        message: message
      });
      return;
    }

    setResult({
      eligible: true,
      message: "You are eligible for the IAL Mathematics qualification (YMA01)!"
    });
  };

  // checkDualEligibility: Handles eligibility logic for dual mode (multiple awards).
  // Contains custom checks for specific dual combinations, then falls back to rule-based logic for all other cases.
  const checkDualEligibility = () => {
    // Define valid applied pairs for IAL
    const validPairs = [
      ["S1", "S2"], ["M1", "M2"], ["S1", "M1"], 
      ["S1", "D1"], ["M1", "D1"]
    ];

    // Check for specific XMA01 & XFM01 combinations (6 units only)
    if (selectedUnits.length === 6) {
      const xma01_xfm01_combinations = [
        ["P1", "P2", "M1", "FP1", "FP2", "FP3"],
        ["P1", "P2", "M1", "FP1", "FP2", "M2"],
        ["P1", "P2", "M1", "FP1", "FP2", "M3"],
        ["P1", "P2", "M1", "FP1", "FP2", "S1"],
        ["P1", "P2", "M1", "FP1", "FP2", "S2"],
        ["P1", "P2", "M1", "FP1", "FP2", "S3"],
        ["P1", "P2", "M1", "FP1", "FP2", "D1"],
        ["P1", "P2", "M1", "FP1", "FP3", "M2"],
        ["P1", "P2", "M1", "FP1", "FP3", "M3"],
        ["P1", "P2", "M1", "FP1", "FP3", "S1"],
        ["P1", "P2", "M1", "FP1", "FP3", "S2"],
        ["P1", "P2", "M1", "FP1", "FP3", "S3"],
        ["P1", "P2", "M1", "FP1", "FP3", "D1"],
        ["P1", "P2", "S1", "FP1", "FP2", "FP3"],
        ["P1", "P2", "S1", "FP1", "FP2", "M1"],
        ["P1", "P2", "S1", "FP1", "FP2", "M2"],
        ["P1", "P2", "S1", "FP1", "FP2", "M3"],
        ["P1", "P2", "S1", "FP1", "FP2", "S2"],
        ["P1", "P2", "S1", "FP1", "FP2", "S3"],
        ["P1", "P2", "S1", "FP1", "FP2", "D1"],
        ["P1", "P2", "S1", "FP1", "FP3", "M1"],
        ["P1", "P2", "S1", "FP1", "FP3", "M2"],
        ["P1", "P2", "S1", "FP1", "FP3", "M3"],
        ["P1", "P2", "S1", "FP1", "FP3", "S2"],
        ["P1", "P2", "S1", "FP1", "FP3", "S3"],
        ["P1", "P2", "S1", "FP1", "FP3", "D1"],
        ["P1", "P2", "D1", "FP1", "FP2", "FP3"],
        ["P1", "P2", "D1", "FP1", "FP2", "M1"],
        ["P1", "P2", "D1", "FP1", "FP2", "M2"],
        ["P1", "P2", "D1", "FP1", "FP2", "M3"],
        ["P1", "P2", "D1", "FP1", "FP2", "S1"],
        ["P1", "P2", "D1", "FP1", "FP2", "S2"],
        ["P1", "P2", "D1", "FP1", "FP2", "S3"],
        ["P1", "P2", "D1", "FP1", "FP3", "M1"],
        ["P1", "P2", "D1", "FP1", "FP3", "M2"],
        ["P1", "P2", "D1", "FP1", "FP3", "M3"],
        ["P1", "P2", "D1", "FP1", "FP3", "S1"],
        ["P1", "P2", "D1", "FP1", "FP3", "S2"],
        ["P1", "P2", "D1", "FP1", "FP3", "S3"],
        // New combinations with P1, P2, FP1 + applied units (no FP2/FP3)
        ["P1", "P2", "M1", "FP1", "M2", "M3"],
        ["P1", "P2", "M1", "FP1", "S1", "S2"],
        ["P1", "P2", "M1", "FP1", "S1", "S3"],
        ["P1", "P2", "M1", "FP1", "S2", "S3"],
        ["P1", "P2", "M1", "FP1", "M2", "D1"],
        ["P1", "P2", "M1", "FP1", "M3", "D1"],
        ["P1", "P2", "M1", "FP1", "S1", "D1"],
        ["P1", "P2", "M1", "FP1", "S2", "D1"],
        ["P1", "P2", "M1", "FP1", "S3", "D1"],
        ["P1", "P2", "S1", "FP1", "M1", "M2"],
        ["P1", "P2", "S1", "FP1", "M1", "M3"],
        ["P1", "P2", "S1", "FP1", "M2", "M3"],
        ["P1", "P2", "S1", "FP1", "S2", "S3"],
        ["P1", "P2", "S1", "FP1", "S2", "D1"],
        ["P1", "P2", "S1", "FP1", "S3", "D1"],
        ["P1", "P2", "S1", "FP1", "M1", "D1"],
        ["P1", "P2", "S1", "FP1", "M2", "D1"],
        ["P1", "P2", "S1", "FP1", "M3", "D1"],
        ["P1", "P2", "D1", "FP1", "M1", "M2"],
        ["P1", "P2", "D1", "FP1", "M1", "M3"],
        ["P1", "P2", "D1", "FP1", "M2", "M3"],
        ["P1", "P2", "D1", "FP1", "S1", "S2"],
        ["P1", "P2", "D1", "FP1", "S1", "S3"],
        ["P1", "P2", "D1", "FP1", "S2", "S3"]
      ];
      
      const sortedSelected = [...selectedUnits].sort();
      for (const combo of xma01_xfm01_combinations) {
        if (combo.slice().sort().every((unit, idx) => unit === sortedSelected[idx])) {
          setResult({
            eligible: true,
            message: "You are eligible for both IAS Mathematics (XMA01) and IAS Further Mathematics (XFM01) qualifications!"
          });
          return;
        }
      }
    }

    // --- Specific YMA01 & YFM01 combinations for 12-unit dual mode ---
    if (selectedUnits.length === 12) {
      const yma01_yfm01_combinations = [
        ["P1", "P2", "P3", "P4", "M1", "S1", "FP1", "FP2", "FP3", "S2", "S3", "D1"],
        ["P1", "P2", "P3", "P4", "M1", "S1", "FP1", "FP2", "FP3", "D1", "M2", "M3"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP2", "M2", "M3", "S1", "S2"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP2", "M2", "M3", "S1", "S3"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP2", "M3", "S1", "S2", "S3"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP3", "M2", "M3", "S1", "S2"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP3", "M2", "M3", "S1", "S3"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP3", "M3", "S1", "S2", "S3"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP2", "FP3", "M2", "M3", "S1"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP2", "FP3", "M3", "S1", "S2"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP2", "FP3", "S1", "S2", "S3"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP2", "M3", "S1", "S2", "S3"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP2", "M3", "S1", "S2", "D1"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP2", "S1", "S2", "S3", "D1"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP3", "M3", "S1", "S2", "S3"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP3", "M3", "S1", "S2", "D1"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP3", "S1", "S2", "S3", "D1"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP2", "FP3", "M3", "S1", "S2"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP2", "FP3", "S1", "S2", "S3"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP2", "FP3", "S2", "S3", "D1"],
        ["P1", "P2", "P3", "P4", "S1", "D1", "FP1", "FP2", "M1", "M2", "M3", "S2"],
        ["P1", "P2", "P3", "P4", "S1", "D1", "FP1", "FP2", "M1", "M2", "M3", "S3"],
        ["P1", "P2", "P3", "P4", "S1", "D1", "FP1", "FP3", "M1", "M2", "M3", "S2"],
        ["P1", "P2", "P3", "P4", "S1", "D1", "FP1", "FP3", "M1", "M2", "M3", "S3"],
        ["P1", "P2", "P3", "P4", "S1", "D1", "FP1", "FP2", "FP3", "M1", "M2", "M3"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP2", "M1", "M2", "M3", "S3"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP2", "M1", "M2", "M3", "D1"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP3", "M1", "M2", "M3", "S3"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP3", "M1", "M2", "M3", "D1"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP2", "FP3", "M1", "M2", "M3"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP2", "FP3", "S3", "D1", "M1"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP2", "FP3", "D1", "M1", "M2"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP2", "FP3", "D1", "M2", "M3"]
      ];
      const sortedSelected = [...selectedUnits].sort();
      for (const combo of yma01_yfm01_combinations) {
        if (combo.slice().sort().every((unit, idx) => unit === sortedSelected[idx])) {
          setResult({ 
            eligible: true, 
            message: "You are eligible for both IAL Mathematics (YMA01) and IAL Further Mathematics (YFM01) qualifications!" 
          });
          return;
        }
      }
    }

    // Define constant arrays at the top
    const pureUnits = ["P1", "P2", "P3", "P4"];
    const furtherPureUnits = ["FP1", "FP2", "FP3"];
    const allAppliedUnits = ["S1", "S2", "S3", "M1", "M2", "M3", "D1"];
    
    // Check Further Pure requirements first
    const hasFP1 = selectedUnits.includes("FP1");
    
    // Count further pure units (FP1, FP2, FP3)
    const selectedFurtherPureUnits = furtherPureUnits.filter(unit => 
      selectedUnits.includes(unit)
    );
    
    // Count all applied units
    const appliedUnits = allAppliedUnits.filter(unit => 
      selectedUnits.includes(unit)
    );

    // Check for P1 and P2
    const hasP1P2 = ["P1", "P2"].every(unit => selectedUnits.includes(unit));

    // Check for all P units
    const hasPureComplete = pureUnits.every(unit => selectedUnits.includes(unit));

    // For YFM01 (IAL Further Mathematics)
    // Must have either:
    // 1. All three FP units plus three applied units, OR
    // 2. Two FP units plus four applied units
    const hasTwoFP = selectedFurtherPureUnits.length >= 2;
    const hasAllFP = selectedFurtherPureUnits.length === 3;
    const totalAppliedUnits = appliedUnits.length;
    const isEligibleForYFM01 = (hasAllFP && totalAppliedUnits >= 3) || 
                              (hasTwoFP && totalAppliedUnits >= 4);

    // For XFM01 (IAS Further Mathematics)
    // Must have FP1 plus two additional units (can be further pure or applied)
    const totalUnitsForXFM01 = selectedFurtherPureUnits.length + appliedUnits.length - 1; // -1 because we don't count FP1 twice
    const isEligibleForXFM01 = hasFP1 && totalUnitsForXFM01 >= 2;
    
    // Check for valid applied pairs for IAL
    const hasValidPair = validPairs.some(pair => 
      pair.every(unit => selectedUnits.includes(unit))
    );
    
    // Check if eligible for IAS Mathematics (XMA01)
    const isEligibleForXMA01 = hasP1P2 && appliedUnits.length >= 1;
    
    // Check if eligible for Pure Mathematics (YPM01)
    // Must have P1, P2, P3, P4, FP1, and (FP2 or FP3)
    const hasFP2 = selectedUnits.includes("FP2");
    const hasFP3 = selectedUnits.includes("FP3");
    const isEligibleForYPM01 = hasPureComplete && selectedUnits.includes("FP1") && (hasFP2 || hasFP3);
    
    // Check if student can get YMA01 (P1-P4 + valid applied pair)
    const canGetYMA01 = hasPureComplete && hasValidPair;

    // Check for specific XFM01 & YFM01 combinations (6 units only)
    if (selectedUnits.length === 6) {
      const xfm01_yfm01_combinations = [
        ["FP1", "FP2", "M1", "M2", "M3", "S1"],
        ["FP1", "FP2", "M1", "M2", "M3", "S2"],
        ["FP1", "FP2", "M1", "M2", "M3", "S3"],
        ["FP1", "FP2", "M1", "M2", "M3", "D1"],
        ["FP1", "FP2", "M2", "M3", "S1", "S2"],
        ["FP1", "FP2", "M2", "M3", "S1", "S3"],
        ["FP1", "FP2", "M2", "M3", "S1", "D1"],
        ["FP1", "FP2", "M3", "S1", "S2", "S3"],
        ["FP1", "FP2", "M3", "S1", "S2", "D1"],
        ["FP1", "FP2", "S1", "S2", "S3", "D1"],
        ["FP1", "FP3", "M1", "M2", "M3", "S1"],
        ["FP1", "FP3", "M1", "M2", "M3", "S2"],
        ["FP1", "FP3", "M1", "M2", "M3", "S3"],
        ["FP1", "FP3", "M1", "M2", "M3", "D1"],
        ["FP1", "FP3", "M2", "M3", "S1", "S2"],
        ["FP1", "FP3", "M2", "M3", "S1", "S3"],
        ["FP1", "FP3", "M2", "M3", "S1", "D1"],
        ["FP1", "FP3", "M3", "S1", "S2", "S3"],
        ["FP1", "FP3", "M3", "S1", "S2", "D1"],
        ["FP1", "FP3", "S1", "S2", "S3", "D1"],
        ["FP1", "FP2", "FP3", "M1", "M2", "M3"],
        ["FP1", "FP2", "FP3", "M2", "M3", "S1"],
        ["FP1", "FP2", "FP3", "M3", "S1", "S2"],
        ["FP1", "FP2", "FP3", "S1", "S2", "S3"],
        ["FP1", "FP2", "FP3", "S2", "S3", "D1"],
        ["FP1", "FP2", "FP3", "S3", "D1", "M1"],
        ["FP1", "FP2", "FP3", "D1", "M1", "M2"],
        ["FP1", "FP2", "FP3", "D1", "M2", "M3"]
      ];
      
      const sortedSelected = [...selectedUnits].sort();
      for (const combo of xfm01_yfm01_combinations) {
        if (combo.slice().sort().every((unit, idx) => unit === sortedSelected[idx])) {
          setResult({
            eligible: true,
            message: "You are eligible for both IAS Further Mathematics (XFM01) and IAL Further Mathematics (YFM01) qualifications!"
          });
          return;
        }
      }
    }
    // Check for specific YMA01 & XMA01 combinations (6 units only)
    if (selectedUnits.length === 6 && selectedFurtherPureUnits.length === 0) {
      const yma01_xma01_combinations = [
        ["P1", "P2", "P3", "P4", "M1", "S1"],
        ["P1", "P2", "P3", "P4", "M1", "D1"],
        ["P1", "P2", "P3", "P4", "M1", "M2"],
        ["P1", "P2", "P3", "P4", "S1", "D1"],
        ["P1", "P2", "P3", "P4", "S1", "S2"]
      ];
      
      const sortedSelected = [...selectedUnits].sort();
      for (const combo of yma01_xma01_combinations) {
        if (combo.slice().sort().every((unit, idx) => unit === sortedSelected[idx])) {
          setResult({
            eligible: true,
            message: "You are eligible for both IAL Mathematics (YMA01) and IAS Mathematics (XMA01) qualifications!"
          });
          return;
        }
      }
    }
    // Check for both YMA01 and YFM01 - but warn if units insufficient for proper dual qualification
    if (canGetYMA01 && isEligibleForYFM01) {
      // Special case: Check if they have exactly P1-P4, FP1, FP2, M1, M2 (8 units)
      const hasExactProblemCombo = selectedUnits.length === 8 &&
        ["P1", "P2", "P3", "P4"].every(unit => selectedUnits.includes(unit)) &&
        ["FP1", "FP2"].every(unit => selectedUnits.includes(unit)) &&
        ["M1", "M2"].every(unit => selectedUnits.includes(unit));
        
      if (hasExactProblemCombo) {
        setResult({
          eligible: false,
          message: "You need 2 more units to be eligible for both IAL Mathematics (YMA01) and IAL Further Mathematics (YFM01). With your current selection, you would need to add 2 more Applied units (such as M3, S1, S2, S3, or D1) to meet the requirements for cashing in both qualifications simultaneously.",
          alternativeMessage: "Alternatively, you could focus on just IAL Mathematics (YMA01) by ensuring you have a valid Applied pair, or add units specifically for IAL Further Mathematics (YFM01)."
        });
        return;
      }
      
      setResult({
        eligible: true,
        message: "You are eligible for both IAL Mathematics (YMA01) and IAL Further Mathematics (YFM01) qualifications!"
      });
      return;
    }
    // Check YMA01 and YPM01 conflict first - special case for dual mode
    if (isEligibleForYPM01 && canGetYMA01) {
      // Check if they have exactly the combination that creates the either/or scenario
      const hasExactConflictCombo = hasPureComplete && hasFP1 && (hasFP2 || hasFP3) && hasValidPair;
      
      if (hasExactConflictCombo) {
        setResult({
          eligible: true,
          message: "You may cash in for either IAL Mathematics (YMA01) and AS Further Mathematics (XFM01), or for IAL Pure Mathematics (YPM01). However, YPM01 must be cashed in during a separate exam series and cannot be combined with YMA01 in the same session.",
          alternativeMessage: "Choose which qualification to cash in based on your academic pathway. Both are valid options with your current unit selection."
        });
        return;
      }
      
      // Original logic for other YMA01/YPM01 conflicts
      let message = "You are eligible for IAL Mathematics (YMA01). ";
      message += "However, YMA01 and YPM01 cannot be cashed in during the same exam series. ";
      message += "To be eligible for IAL Further Mathematics (YFM01) instead, you would need ";
      
      // Calculate what they need for YFM01
      const fpUnitsCount = selectedFurtherPureUnits.length;
      const appliedCount = totalAppliedUnits;
      
      if (fpUnitsCount < 2) {
        const fpNeeded = 2 - fpUnitsCount;
        message += `${fpNeeded} more Further Pure unit${fpNeeded > 1 ? 's' : ''}`;
        if (appliedCount < 4) {
          message += ` and ${4 - appliedCount} more Applied unit${(4 - appliedCount) > 1 ? 's' : ''}`;
        }
      } else if (fpUnitsCount === 2 && appliedCount < 4) {
        message += `${4 - appliedCount} more Applied unit${(4 - appliedCount) > 1 ? 's' : ''}`;
      } else if (fpUnitsCount === 3 && appliedCount < 3) {
        message += `${3 - appliedCount} more Applied unit${(3 - appliedCount) > 1 ? 's' : ''}`;
      }
      
      message += ".";
      
      setResult({
        eligible: true,
        message: message,
        qualification: "IAL Mathematics (YMA01)",
        alternativeMessage: "Note: YPM01 and YMA01 cannot be taken in the same exam series. Consider YFM01 as an alternative."
      });
      return;
    }
    // Check for specific YMA01 & XFM01 combinations (9 units only)
    if (selectedUnits.length === 9) {
      const yma01_xfm01_combinations = [
        ["P1", "P2", "P3", "P4", "M1", "S1", "FP1", "FP2", "FP3"],
        ["P1", "P2", "P3", "P4", "M1", "S1", "FP1", "FP2", "M2"],
        ["P1", "P2", "P3", "P4", "M1", "S1", "FP1", "FP2", "M3"],
        ["P1", "P2", "P3", "P4", "M1", "S1", "FP1", "FP2", "S2"],
        ["P1", "P2", "P3", "P4", "M1", "S1", "FP1", "FP2", "S3"],
        ["P1", "P2", "P3", "P4", "M1", "S1", "FP1", "FP2", "D1"],
        ["P1", "P2", "P3", "P4", "M1", "S1", "FP1", "FP3", "M2"],
        ["P1", "P2", "P3", "P4", "M1", "S1", "FP1", "FP3", "M3"],
        ["P1", "P2", "P3", "P4", "M1", "S1", "FP1", "FP3", "S2"],
        ["P1", "P2", "P3", "P4", "M1", "S1", "FP1", "FP3", "S3"],
        ["P1", "P2", "P3", "P4", "M1", "S1", "FP1", "FP3", "D1"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP2", "FP3"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP2", "M2"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP2", "M3"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP2", "S1"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP2", "S2"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP2", "S3"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP3", "M2"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP3", "M3"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP3", "S1"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP3", "S2"],
        ["P1", "P2", "P3", "P4", "M1", "D1", "FP1", "FP3", "S3"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP2", "FP3"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP2", "M3"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP2", "S1"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP2", "S2"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP2", "S3"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP2", "D1"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP3", "M3"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP3", "S1"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP3", "S2"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP3", "S3"],
        ["P1", "P2", "P3", "P4", "M1", "M2", "FP1", "FP3", "D1"],
        ["P1", "P2", "P3", "P4", "S1", "D1", "FP1", "FP2", "FP3"],
        ["P1", "P2", "P3", "P4", "S1", "D1", "FP1", "FP2", "M1"],
        ["P1", "P2", "P3", "P4", "S1", "D1", "FP1", "FP2", "M2"],
        ["P1", "P2", "P3", "P4", "S1", "D1", "FP1", "FP2", "M3"],
        ["P1", "P2", "P3", "P4", "S1", "D1", "FP1", "FP2", "S2"],
        ["P1", "P2", "P3", "P4", "S1", "D1", "FP1", "FP2", "S3"],
        ["P1", "P2", "P3", "P4", "S1", "D1", "FP1", "FP3", "M1"],
        ["P1", "P2", "P3", "P4", "S1", "D1", "FP1", "FP3", "M2"],
        ["P1", "P2", "P3", "P4", "S1", "D1", "FP1", "FP3", "M3"],
        ["P1", "P2", "P3", "P4", "S1", "D1", "FP1", "FP3", "S2"],
        ["P1", "P2", "P3", "P4", "S1", "D1", "FP1", "FP3", "S3"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP2", "FP3"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP2", "M1"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP2", "M2"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP2", "M3"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP2", "S3"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP2", "D1"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP3", "M1"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP3", "M2"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP3", "M3"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP3", "S3"],
        ["P1", "P2", "P3", "P4", "S1", "S2", "FP1", "FP3", "D1"]
      ];
      
      const sortedSelected = [...selectedUnits].sort();
      for (const combo of yma01_xfm01_combinations) {
        if (combo.slice().sort().every((unit, idx) => unit === sortedSelected[idx])) {
          setResult({
            eligible: true,
            message: "You may cash in for either IAL Mathematics (YMA01) and AS Further Mathematics (XFM01), or for IAL Pure Mathematics (YPM01). However, YPM01 must be cashed in during a separate exam series and cannot be combined with YMA01 in the same session."
          });
          return;
        }
      }
    }
    // Check if eligible for YFM01 alone
    if (isEligibleForYFM01) {
      setResult({
        eligible: true,
        message: "You are eligible for the IAL Further Mathematics qualification (YFM01)!"
      });
      return;
    }
    // Check if eligible for YPM01 alone
    if (isEligibleForYPM01 && selectedUnits.length >= 6) {
      setResult({
        eligible: true,
        message: "You are eligible for the IAL Pure Mathematics qualification (YPM01)!",
        qualification: "IAL Pure Mathematics (YPM01)"
      });
      return;
    }
    // Suppress single-qualification IAS results in dual mode
    // Check if eligible for XFM01 alone
    if (isEligibleForXFM01) {
      setResult({
        eligible: false,
        message: "In dual mode, only dual-qualification or IAL awards are shown. You do not currently qualify for a dual award with the selected units."
      });
      return;
    }
    // Check for XPM01 eligibility (P1, P2, and FP1)
    if (hasP1P2 && hasFP1 && !hasPureComplete) {
      setResult({
        eligible: false,
        message: "In dual mode, only dual-qualification or IAL awards are shown. You do not currently qualify for a dual award with the selected units."
      });
      return;
    }
    // If they have FP1 but not enough units, give specific guidance
    if (hasFP1) {
      if (hasTwoFP) {
        const appliedUnitsNeeded = 4 - totalAppliedUnits;
        setResult({
          eligible: false,
          message: `You have two Further Pure units but need ${appliedUnitsNeeded} more applied unit${appliedUnitsNeeded > 1 ? 's' : ''} to be eligible for IAL Further Mathematics (YFM01).`
        });
      } else {
        setResult({
          eligible: false,
          message: "In dual mode, only dual-qualification or IAL awards are shown. You do not currently qualify for a dual award with the selected units."
        });
      }
      return;
    }
    // Check Pure Mathematics requirements if not eligible for Further Mathematics
    if (!hasPureComplete) {
      setResult({
        eligible: false,
        message: "Missing one or more required Pure Mathematics units (P1-P4) for IAL Mathematics."
      });
      return;
    }
    // Check if eligible for IAS Mathematics alone
    if (!hasPureComplete && !hasFP1) {
      setResult({
        eligible: false,
        message: "Missing required Pure Mathematics units (P1-P4) for IAL Mathematics and FP1 for Further Mathematics."
      });
      return;
    }
    if (!hasFP1) {
      setResult({
        eligible: false,
        message: "FP1 is required for Further Mathematics. You do not currently qualify for Further Mathematics or IAL Mathematics (YMA01)."
      });
      return;
    }
    // Need at least 2 more units beyond FP1
    const additionalUnitsNeeded = 2;
    const additionalUnitsAvailable = totalAppliedUnits;
    if (additionalUnitsAvailable < additionalUnitsNeeded) {
      setResult({
        eligible: false,
        message: `You need ${additionalUnitsNeeded} additional units beyond FP1 for Further Mathematics. You currently have ${additionalUnitsAvailable}. You do not currently qualify for IAL Mathematics (YMA01) or Further Mathematics.`
      });
      return;
    }
    // --- Rule-based check for YMA01 & YFM01 in dual mode ---
    // YMA01: P1-P4 and a valid applied pair
    // YFM01: (FP1, FP2, FP3) + 3 applied OR any 2 FP + 4 applied
    const hasAllPure = pureUnits.every(unit => selectedUnits.includes(unit));
    const appliedCount = allAppliedUnits.filter(unit => selectedUnits.includes(unit)).length;
    const fpCount = furtherPureUnits.filter(unit => selectedUnits.includes(unit)).length;
    const hasYFM01 = (hasAllFP && appliedCount >= 3) || (hasTwoFP && appliedCount >= 4);
    const hasYMA01 = hasAllPure && validPairs.some(pair => pair.every(unit => selectedUnits.includes(unit)));
    if (hasYMA01 && hasYFM01 && selectedUnits.length === 12) {
      setResult({ eligible: true, message: "You are eligible for the YMA01 & YFM01!" });
      return;
    }
    // If no qualification is met, show a clear ineligible message
    setResult({
      eligible: false,
      message: "You are not eligible for any dual IAL Mathematics qualification with the selected units. Please review the requirements for each qualification and ensure you have the correct combination of Pure, Further Pure, and Applied Mathematics units. If you need help, consult the official Pearson guidance or your examination officer."
    });
  };

  // --- UI Rendering Functions ---
  // These functions render different sections of the calculator UI, such as the mode selector,
  // information banner, qualification selector, unit selection panels, additional resources, and start over button.
  // Each function returns a JSX block for its respective UI section.

  const renderModeSelector = () => {
    if (qualificationMode === 'dual') return null; // Hide mode selector in dual mode
    return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-[#4A1D7A] mb-3">Qualification Mode</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            className={`px-4 py-2 rounded-md ${
              qualificationMode === 'standard'
                ? 'bg-[#4A1D7A] text-white'
                : 'bg-[#D1C4E9] text-[#111827] hover:bg-[#9B7FCB] hover:text-white'
            }`}
            onClick={() => setQualificationMode('standard')}
          >
            Standard Mathematics Eligibility
          </button>
          <button
            className={`px-4 py-2 rounded-md ${
              qualificationMode === 'dual'
                ? 'bg-[#4A1D7A] text-white'
                : 'bg-[#D1C4E9] text-[#111827] hover:bg-[#9B7FCB] hover:text-white'
            }`}
            onClick={() => setQualificationMode('dual')}
          >
            Dual Qualification Mode
          </button>
        </div>
        {qualificationMode === 'standard' ? (
          <div className="bg-[#E6F7FF] p-4 rounded-lg border-l-4 border-[#0369A1]">
            <p className="text-sm text-[#111827]">
              <strong>Standard Mode:</strong> Check eligibility for Mathematics (YMA01), Further Mathematics (YFM01), or IAS Further Mathematics qualifications. This mode now includes support for qualification combinations and transfer of credit options.
            </p>
          </div>
        ) : null}
      </div>
    );
  };

  const renderQualificationSelector = () => {
    if (qualificationMode === 'dual') return null; // Hide qualification selector in dual mode
    return (
      <div className="mb-6">
        <button
          onClick={() => setShowQualOptions(!showQualOptions)}
          className="w-full flex items-center justify-between bg-[#9B7FCB] hover:bg-[#4A1D7A] text-white p-3 rounded-md cursor-pointer transition-colors"
        >
          <h3 className="font-medium">Additional Qualification Options</h3>
          <svg 
            className={`w-5 h-5 transition-transform ${showQualOptions ? 'transform rotate-180' : ''}`}
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {showQualOptions && (
          <div className="mt-3 border border-[#9B7FCB] rounded-md p-4 bg-white">
            <p className="mb-3 text-sm">Select the combination of qualifications you are aiming for:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div 
                className={`p-4 rounded-md cursor-pointer border ${
                  selectedQualification === 'ial+ias' 
                    ? 'bg-[#ECFDF5] border-[#059669]' 
                    : 'bg-white border-grey-300 hover:bg-[#F3F4F6]'
                }`}
                onClick={() => setSelectedQualification('ial+ias')}
              >
                <h4 className="font-semibold">IAL Mathematics + IAS Further Mathematics</h4>
                <p className="text-sm text-grey-700">Full A Level Mathematics with AS Level Further Mathematics</p>
              </div>
              
              <div 
                className={`p-4 rounded-md cursor-pointer border ${
                  selectedQualification === 'transfer' 
                    ? 'bg-[#ECFDF5] border-[#059669]' 
                    : 'bg-white border-grey-300 hover:bg-[#F3F4F6]'
                }`}
                onClick={() => setSelectedQualification('transfer')}
              >
                <h4 className="font-semibold">Transfer of Credit</h4>
                <p className="text-sm text-grey-700">Using qualifications from other exam boards</p>
              </div>
            </div>
            
            {selectedQualification === 'ial+ias' && (
              <div className="bg-[#FEF9C3] p-4 rounded-lg mt-4 border-l-4 border-[#CA8A04]">
                <h4 className="font-semibold text-[#111827]">Important Reminder for IAL + IAS Combinations</h4>
                <p className="text-sm mt-1 text-[#111827]">
                  If you have previously cashed in IAS Mathematics or Further Mathematics, you must uncash these qualifications
                  when cashing in the IAL options to allow reaggregation of units.
                </p>
                <p className="text-sm mt-2 text-[#111827]">
                  Your examination officer can help with the uncashing process, which must be completed before the IAL qualification
                  can be awarded.
                </p>
              </div>
            )}
            
            {selectedQualification === 'transfer' && (
              <div className="bg-[#E6F7FF] p-4 rounded-lg mt-4 border border-[#0369A1]">
                <h4 className="font-semibold text-[#111827]">Transfer of Credit Information</h4>
                <p className="text-sm mt-1 text-[#111827]">
                  If you are cashing in Edexcel IAL Mathematics with units from another exam board, you'll need to follow the Transfer
                  of Credit process.
                </p>
                <a 
                  href="https://qualifications.pearson.com/en/support/support-topics/exams/special-requirements/transfer-of-credit.html" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm mt-2 text-[#0369A1] hover:underline inline-block"
                >
                  Visit the Transfer of Credit page →
                </a>
                
                <h4 className="font-semibold mt-4 text-[#111827]">Transfer of Credit Key Points:</h4>
                <ul className="list-disc pl-5 text-sm mt-1 text-[#111827]">
                  <li>You must apply for Transfer of Credit before the qualification can be awarded</li>
                  <li>Applications must be made via your examination officer</li>
                  <li>Evidence of the previously achieved qualification must be provided</li>
                  <li>Check deadline dates carefully - applications must be received before results day</li>
                </ul>
              </div>
            )}
            
            {selectedQualification === 'ial+ias' && (
              <div className="mt-4">
                <h4 className="font-semibold text-[#111827]">IAL Mathematics + IAS Further Mathematics Requirements:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <div>
                    <h5 className="font-medium text-[#111827]">For IAL Mathematics:</h5>
                    <ul className="list-disc pl-5 text-sm text-[#111827]">
                      <li>P1, P2, P3, and P4</li>
                      <li>One valid pair from the applied units</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium text-[#111827]">For IAS Further Mathematics:</h5>
                    <ul className="list-disc pl-5 text-sm text-[#111827]">
                      <li>FP1 is required</li>
                      <li>Two more units (cannot include P1-P4)</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Collapsible and filterable additional resources
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const resources = [
    {
      title: "IAL Mathematics Aggregation Rules and Guidance",
      url: "https://qualifications.pearson.com/content/dam/pdf/International%20Advanced%20Level/Mathematics/2018/Teaching-and-Learning-Materials/aggregation-rules-and-guidance.pdf",
      icon: "📄",
      description: "Official Pearson PDF guidance on aggregation rules. Note: YMA01 and YPM01 cannot be cashed in during the same exam series."
    },
    {
      title: "Edexcel IAL Mathematics Qualification Page",
      url: "https://qualifications.pearson.com/en/qualifications/edexcel-international-advanced-levels/mathematics.html",
      icon: "🔗",
      description: "Pearson's main page for IAL Mathematics qualifications."
    },
    {
      title: "Transfer of Credit Information",
      url: "https://qualifications.pearson.com/en/support/support-topics/exams/special-requirements/transfer-of-credit.html",
      icon: "🔄",
      description: "Pearson's official information on transfer of credit."
    }
  ];
  const renderAdditionalResources = () => (
    <div className="w-full">
      <button
        className="w-full flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-800 p-4 rounded-lg hover:from-green-100 hover:to-emerald-100 transition-colors"
        onClick={() => setResourcesOpen(open => !open)}
        aria-expanded={resourcesOpen}
        aria-controls="resources-panel"
      >
        <div className="flex items-center">
          <span className="text-2xl mr-3">📚</span>
          <span className="font-semibold">Additional Resources</span>
        </div>
        <svg className={`w-5 h-5 transition-transform ${resourcesOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {resourcesOpen && (
        <div id="resources-panel" className="mt-4 space-y-3">
          {resources.length === 0 ? (
            <p className="text-sm text-grey-700">No resources found.</p>
          ) : (
            resources.map(resource => (
              <a
                key={resource.url}
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start bg-white p-4 rounded-lg border border-grey-200 hover:border-blue-300 hover:bg-blue-50 transition-colors group"
              >
                <span className="text-2xl mr-3 group-hover:scale-110 transition-transform">{resource.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-blue-700 group-hover:text-blue-900">{resource.title}</div>
                  <div className="text-sm text-grey-600 mt-1">{resource.description}</div>
                </div>
                <svg className="w-5 h-5 text-grey-400 group-hover:text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            ))
          )}
        </div>
      )}
    </div>
  );
  
  const renderStartOverButton = () => (
    <button 
      onClick={onStartOver}
      className="btn-secondary flex items-center justify-center min-w-[140px]"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
      </svg>
      Start Over
    </button>
  );

  // getSmartRecommendations: Provides intelligent recommendations based on current unit selection
  const getSmartRecommendations = () => {
    const recommendations = [];
    const pureUnits = ["P1", "P2", "P3", "P4"];
    const furtherPureUnits = ["FP1", "FP2", "FP3"];
    const appliedUnits = ["S1", "S2", "S3", "M1", "M2", "M3", "D1"];
    
    const selectedPure = selectedUnits.filter(unit => pureUnits.includes(unit));
    const selectedFurther = selectedUnits.filter(unit => furtherPureUnits.includes(unit));
    const selectedApplied = selectedUnits.filter(unit => appliedUnits.includes(unit));

    if (selectedUnits.length === 0) return recommendations;

    // Recommendations for completing IAL Mathematics
    if (selectedPure.length > 0 && selectedPure.length < 4) {
      const missingPure = pureUnits.filter(unit => !selectedUnits.includes(unit));
      if (missingPure.length <= 2) {
        recommendations.push({
          icon: "📚",
          text: `Add ${missingPure.join(" and ")} to complete Pure Mathematics requirements for IAL`
        });
      }
    }

    // Recommendations for valid applied pairs
    if (selectedPure.length >= 2 && selectedApplied.length === 1) {
      const validPairs = [
        ["S1", "S2"], ["M1", "M2"], ["S1", "M1"], 
        ["S1", "D1"], ["M1", "D1"]
      ];
      const currentApplied = selectedApplied[0];
      const possiblePartners = [];
      
      validPairs.forEach(pair => {
        if (pair.includes(currentApplied)) {
          const partner = pair.find(unit => unit !== currentApplied);
          if (!selectedUnits.includes(partner)) {
            possiblePartners.push(partner);
          }
        }
      });
      
      if (possiblePartners.length > 0) {
        recommendations.push({
          icon: "🔗",
          text: `Consider adding ${possiblePartners.join(" or ")} to create a valid applied pair with ${currentApplied}`
        });
      }
    }

    // IAS Mathematics pathway
    if (selectedPure.includes("P1") && selectedPure.includes("P2") && selectedApplied.length >= 1) {
      if (selectedPure.length < 4) {
        recommendations.push({
          icon: "⭐",
          text: "You're eligible for IAS Mathematics (XMA01) with your current selection!"
        });
      }
    }

    // Further Mathematics pathway
    if (selectedFurther.includes("FP1")) {
      const totalFurtherApplied = selectedFurther.length + selectedApplied.length - 1; // -1 because FP1 is counted separately
      
      if (totalFurtherApplied < 2) {
        const needed = 2 - totalFurtherApplied;
        recommendations.push({
          icon: "🚀",
          text: `Add ${needed} more Further Pure or Applied unit${needed > 1 ? 's' : ''} for IAS Further Mathematics (XFM01)`
        });
      } else if (selectedFurther.length >= 2 && selectedApplied.length >= 4) {
        recommendations.push({
          icon: "🏆",
          text: "You may be eligible for IAL Further Mathematics (YFM01) - check requirements!"
        });
      }
    }

    // Pure Mathematics pathway
    if (selectedPure.length === 4 && selectedFurther.includes("FP1")) {
      if (!selectedFurther.includes("FP2") && !selectedFurther.includes("FP3")) {
        recommendations.push({
          icon: "🎓",
          text: "Add FP2 or FP3 to be eligible for IAL Pure Mathematics (YPM01)"
        });
      }
    }

    // General progression advice
    if (selectedUnits.length >= 6) {
      recommendations.push({
        icon: "✨",
        text: "You have selected many units - consider checking eligibility to see all possible qualifications"
      });
    }

    return recommendations.slice(0, 3); // Limit to 3 recommendations to avoid clutter
  };

  // handlePageFeedback: Handles feedback for the overall page experience
  const handlePageFeedback = (isPositive) => {
    // Track feedback
    ReactGA.event({
      category: 'Feedback',
      action: isPositive ? 'Page Thumbs Up' : 'Page Thumbs Down',
      label: `Selected Units: ${selectedUnits.length}`
    });
    
    setFeedbackGiven(true);
    
    // Hide feedback after 3 seconds
    setTimeout(() => {
      setFeedbackGiven(false);
    }, 3000);
  };

  // renderPageFeedback: Renders the subtle page feedback component
  const renderPageFeedback = () => {
    if (selectedUnits.length === 0) return null;
    
    return (
      <div className="fixed bottom-4 right-4 z-40">
        <div className="bg-white rounded-lg shadow-lg border border-grey-200 p-3">
          {!feedbackGiven ? (
            <div className="flex items-center gap-3">
              <p className="text-xs text-grey-600 font-medium">How is your experience?</p>
              <div className="flex gap-2">
                <button 
                  onClick={() => handlePageFeedback(true)}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50 p-1 rounded transition-colors"
                  aria-label="Good experience"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M7.493 18.75c-.425 0-.82-.236-.975-.632A7.48 7.48 0 016 15.375c0-1.75.599-3.358 1.602-4.634.151-.192.373-.309.6-.397.473-.183.89-.514 1.212-.924a9.042 9.042 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75 2.25 2.25 0 012.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558-.629 1.08-1.06 1.533l-.149.15c-.16.16-.33.317-.514.463 1.42.384 2.522 1.400 2.522 2.186 0 .996-.506 1.70-.944 2.218a3.825 3.825 0 01-.957.832c-.95.564-1.54 1.555-1.54 2.673v.05c0 .273-.035.54-.102.798-.315 1.195-1.387 2.027-2.686 2.027z"/>
                  </svg>
                </button>
                <button 
                  onClick={() => handlePageFeedback(false)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors"
                  aria-label="Poor experience"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M15.73 5.25h1.035A7.465 7.465 0 0118 9.375a7.465 7.465 0 01-1.235 4.125h-.148c-.806 0-1.534.446-2.031 1.08a9.04 9.04 0 01-2.861 2.4c-.723.384-1.35.956-1.653 1.715a4.498 4.498 0 00-.322 1.672V21a.75.75 0 01-.75.75 2.25 2.25 0 01-2.25-2.25c0-1.152.26-2.243.723-3.218C7.74 15.724 7.366 15.202 7.366 14.625c0-.996.506-1.70.944-2.218a3.825 3.825 0 01.957-.832C10.217 10.811 10.807 9.82 10.807 8.702v-.05c0-.273.035-.541.102-.798.315-1.195 1.387-2.027 2.686-2.027h.9c.825 0 1.506.603 1.506 1.348 0 .729-.78 1.25-1.506 1.25h-.9c-.825 0-1.506.603-1.506 1.348 0 .729.78 1.25 1.506 1.25h2.04z"/>
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs text-green-600 font-medium">Thanks for your feedback!</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSection = (sectionKey, sectionTitle, sectionUnits) => (
    <div className="modern-card slide-up">
      <div
        className="section-header flex items-center justify-between"
        onClick={() => toggleExpand(sectionKey)}
        aria-expanded={expandStates[sectionKey]}
        aria-controls={`${sectionKey}-panel`}
        tabIndex={0}
        role="button"
        onKeyPress={e => { if (e.key === 'Enter' || e.key === ' ') toggleExpand(sectionKey); }}
      >
        <div className="flex items-center">
          <span className="text-lg font-semibold">{sectionTitle}</span>
          <span className="ml-3 bg-white bg-opacity-20 rounded-full px-2 py-1 text-sm">
            {sectionUnits.filter(unit => selectedUnits.includes(unit.code)).length}/{sectionUnits.length}
          </span>
        </div>
        <div className="flex items-center">
          <span className="text-sm mr-2 opacity-80">
            {expandStates[sectionKey] ? 'Hide' : 'Show'} units
          </span>
          <svg className={`w-5 h-5 transition-transform ${expandStates[sectionKey] ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      
      {expandStates[sectionKey] && (
        <div id={`${sectionKey}-panel`} className="p-6 bg-white rounded-b-lg">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sectionUnits.map(unit => (
              <div key={unit.code} className="tooltip">
                <button
                  onClick={() => toggleUnit(unit.code)}
                  className={`unit-card w-full text-left ${
                    selectedUnits.includes(unit.code) ? 'selected' : ''
                  }`}
                  aria-pressed={selectedUnits.includes(unit.code)}
                  aria-label={`${unit.code}: ${unit.name}`}
                >
                  <div className="relative z-10">
                    <div className="font-bold text-lg mb-1">{unit.code}</div>
                    <div className="text-sm font-normal opacity-90">{unit.name}</div>
                    {selectedUnits.includes(unit.code) && (
                      <div className="absolute top-2 right-2">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                </button>
                <span className="tooltiptext">
                  <strong>{unit.code}</strong><br/>
                  {unit.name}<br/>
                  <em className="text-sm opacity-80">Click to {selectedUnits.includes(unit.code) ? 'deselect' : 'select'}</em>
                </span>
              </div>
            ))}
          </div>
          
          {/* Section Summary */}
          <div className="mt-6 pt-4 border-t border-grey-200">
            <div className="flex items-center justify-between text-sm text-grey-600">
              <span>
                {sectionUnits.filter(unit => selectedUnits.includes(unit.code)).length} of {sectionUnits.length} units selected
              </span>
              {sectionUnits.filter(unit => selectedUnits.includes(unit.code)).length > 0 && (
                <button
                  onClick={() => {
                    const sectionCodes = sectionUnits.map(unit => unit.code);
                    const deselectedUnits = selectedUnits.filter(code => !sectionCodes.includes(code));
                    setSelectedUnits(deselectedUnits);
                  }}
                  className="text-red-600 hover:text-red-800 font-medium text-xs"
                >
                  Clear Section
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="font-plus-jakarta">
      {/* Main Container */}
      <div className="max-w-6xl mx-auto p-4 space-y-6">
        
        {/* Header Section */}
        <div className="modern-card slide-up">
          <div className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-bold text-[#4A1D7A] mb-2">Mathematics Eligibility Calculator</h2>
                <p className="text-grey-600">Select your units to check qualification eligibility</p>
              </div>
              <div className="progress-indicator">
                <div className="progress-step current">
                  <div className="progress-step-number">1</div>
                  <span className="font-medium">Select Units</span>
                </div>
              </div>
            </div>
            
            {/* Mode Information */}
            {isSingleCode && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 text-blue-800 p-4 rounded-lg mb-4 fade-in">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <div>
                    <strong className="block">Single Code Mode</strong>
                    <span className="text-sm">You are cashing in a single code. Only the relevant options are shown below.</span>
                  </div>
                </div>
              </div>
            )}
            {isDualCode && (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 text-purple-800 p-4 rounded-lg mb-4 fade-in">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-purple-600 font-bold">M</span>
                  </div>
                  <div>
                    <strong className="block">Multiple Codes Mode</strong>
                    <span className="text-sm">You are cashing in multiple codes. Dual mode options are enabled.</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Content Layout */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          
          {/* Left Column - Unit Selection */}
          <div className="xl:col-span-3 space-y-4">
            {!isSingleCode && renderModeSelector()}
            {!isSingleCode && renderQualificationSelector()}
            
            {/* Unit Sections */}
            {renderSection("pure", "Pure Mathematics", units.pure)}
            {renderSection("further", "Further Pure Mathematics", units.further)}
            {renderSection("applied", "Applied Mathematics", units.applied)}
          </div>
          
          {/* Right Column - Status Panel */}
          <div className="xl:col-span-1">
            <div className="sticky top-6 space-y-4">
              
              {/* Selected Units Summary */}
              <div className="glass-panel p-4 slide-up">
                <h3 className="font-semibold text-lg text-[#4A1D7A] mb-3 flex items-center">
                  <span className="w-6 h-6 bg-[#4A1D7A] rounded-full flex items-center justify-center text-white text-sm mr-2">
                    {selectedUnits.length}
                  </span>
                  Selected Units
                </h3>
                {selectedUnits.length === 0 ? (
                  <p className="text-grey-500 text-sm italic">No units selected yet</p>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedUnits.map(unit => {
                      const unitInfo = [...units.pure, ...units.further, ...units.applied].find(u => u.code === unit);
                      return (
                        <div key={unit} className="tooltip">
                          <div className="bg-gradient-to-r from-[#4A1D7A] to-[#5F259F] text-white px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-between">
                            <span>{unit}</span>
                            <button
                              onClick={() => toggleUnit(unit)}
                              className="ml-2 w-4 h-4 hover:bg-white hover:text-[#4A1D7A] rounded transition-colors flex items-center justify-center text-xs"
                              aria-label={`Remove ${unit}`}
                            >
                              ×
                            </button>
                          </div>
                          {unitInfo && (
                            <span className="tooltiptext">{unitInfo.name}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Quick Statistics */}
                {selectedUnits.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-grey-200 text-xs text-grey-600">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <div className="font-semibold text-[#4A1D7A]">{units.pure.filter(u => selectedUnits.includes(u.code)).length}</div>
                        <div>Pure</div>
                      </div>
                      <div>
                        <div className="font-semibold text-[#4A1D7A]">{units.further.filter(u => selectedUnits.includes(u.code)).length}</div>
                        <div>Further</div>
                      </div>
                      <div>
                        <div className="font-semibold text-[#4A1D7A]">{units.applied.filter(u => selectedUnits.includes(u.code)).length}</div>
                        <div>Applied</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Check Eligibility Button */}
              <button 
                className="btn-primary w-full"
                onClick={checkIALEligibility}
                disabled={selectedUnits.length === 0}
              >
                {selectedUnits.length === 0 ? 'Select Units First' : 'Check Eligibility'}
              </button>

              {/* Smart Recommendations */}
              <div className="glass-panel p-4">
                <h4 className="font-semibold text-[#4A1D7A] mb-2 flex items-center">
                  <span className="mr-2">🎯</span>
                  Smart Recommendations
                </h4>
                {selectedUnits.length === 0 ? (
                  <p className="text-sm text-grey-600">Select units to see personalised recommendations</p>
                ) : (
                  <div className="space-y-2">
                    {getSmartRecommendations().map((rec, index) => (
                      <div key={index} className="text-xs bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded p-2">
                        <div className="flex items-start">
                          <span className="mr-1 flex-shrink-0">{rec.icon}</span>
                          <span className="text-blue-800">{rec.text}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Help Section */}
              <div className="glass-panel p-4">
                <h4 className="font-semibold text-[#4A1D7A] mb-2 flex items-center">
                  <span className="mr-2">💡</span>
                  Quick Help
                </h4>
                <ul className="text-sm text-grey-600 space-y-1">
                  <li>• Click units to select or deselect</li>
                  <li>• Check smart recommendations as you select</li>
                  <li>• Hover over units for detailed descriptions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="modern-card slide-up">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="flex-1">
                {renderAdditionalResources()}
              </div>
              <div className="w-full sm:w-auto">
                {renderStartOverButton()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Show eligibility result panel */}
      <ResultPanel result={result} />
      
      {/* Page feedback component */}
      {renderPageFeedback()}
    </div>
  );
}

export default Calculator;

// --- End of Calculator component ---
// This component manages all state, logic, and UI for the IAL Maths Eligibility Calculator.
// It is designed to be maintainable and clear for future developers, with detailed comments throughout.
// All eligibility logic and UI rendering is self-contained in this file. 
